import { validateDebitCardNumber } from "../../../../utils/payment"
import { toSavedCreditProfileIds } from "../helper"

export const ACCOUNT_NUMBER_MIN_LENGTH = 8
export const ACCOUNT_NUMBER_MAX_LENGTH = 17
export const ACCOUNT_NUMBER_LENGTH_MESSAGE = "Must be 8 to 17 characters"
export const ACCOUNT_NUMBER_MAX_LENGTH_MESSAGE = "Must be 17 characters or fewer"

const isBlank = (value) =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "")

const hasInvalidAccountNumberLength = (value) => {
  const length = String(value).trim().length
  return length < ACCOUNT_NUMBER_MIN_LENGTH || length > ACCOUNT_NUMBER_MAX_LENGTH
}

const isAccountNumberTooLong = (value) => String(value).trim().length > ACCOUNT_NUMBER_MAX_LENGTH

export const STRICT_DEBT_RULES = [
  { key: "accountType", message: "Account Type required", required: true, isMissing: (field) => !field?.accountType },
  { key: "accountNumber", message: "Account Number required", required: true, isMissing: (field) => !field?.accountNumber },
  { key: "openedDate", message: "Opened Date required", required: true, isMissing: (field) => !field?.openedDate },
  { key: "reportedDate", message: "Reported Date required", required: true, isMissing: (field) => !field?.reportedDate },
  { key: "closedDate", message: "Closed Date required", required: true, isMissing: (field) => !field?.closedDate },
  { key: "lastPaymentDate", message: "Last Payment Date required", required: true, isMissing: (field) => !field?.lastPaymentDate },
  {
    key: "accountStatus",
    message: "Account Status required",
    required: true,
    isMissing: (field) => (field?.accountStatus === false ? false : !field?.accountStatus)
  },
  { key: "isOpenAccount", message: "Payment Status required", required: true, isMissing: (field) => field?.isOpenAccount?.length === 0 },
  { key: "creditorName", message: "Creditor Name required", required: true, isMissing: (field) => !field?.creditorName },
  { key: "balanceAmount", message: "Balance Owed required", required: true, isMissing: (field) => !field?.balanceAmount },
  { key: "pastDueAmount", message: "Past Due Amount required", required: true, isMissing: (field) => !field?.pastDueAmount },
  { key: "creditLimit", message: "Credit Limit required", required: true, isMissing: (field) => !field?.creditLimit },
]

export const MANUAL_DEBT_RULES = [
  { key: "accountType", message: "Account Type required", required: true, isMissing: (field) => isBlank(field?.accountType) },
  { key: "accountNumber", message: "Account Number required", required: true, isMissing: (field) => isBlank(field?.accountNumber) },
  {
    key: "accountNumber",
    message: ACCOUNT_NUMBER_LENGTH_MESSAGE,
    required: true,
    isMissing: (field) => !isBlank(field?.accountNumber) && hasInvalidAccountNumberLength(field.accountNumber)
  },
  { key: "openedDate", message: "Opened Date required", required: true, isMissing: (field) => isBlank(field?.openedDate) },
  { key: "reportedDate", message: "Reported Date required", required: true, isMissing: (field) => isBlank(field?.reportedDate) },
  {
    key: "closedDate",
    message: "Closed Date required",
    required: false,
    isMissing: (field) => field?.isOpenAccount === false && isBlank(field?.closedDate)
  },
  {
    key: "accountStatus",
    message: "Account Status required",
    required: true,
    isMissing: (field) => (field?.accountStatus === false ? false : isBlank(field?.accountStatus))
  },
  {
    key: "isOpenAccount",
    message: "Payment Status required",
    required: true,
    isMissing: (field) => field?.isOpenAccount !== true && field?.isOpenAccount !== false
  },
  { key: "creditorName", message: "Creditor Name required", required: true, isMissing: (field) => isBlank(field?.creditorName) },
  { key: "balanceAmount", message: "Balance Owed required", required: true, isMissing: (field) => isBlank(field?.balanceAmount) },
]

// WS #2230103: Solar account numbers are often shorter than 8, so behind CREATE_DEBTS_ACCOUNT_NUMBER_NO_MIN
// only the maximum stays. Every other rule is the same object as in MANUAL_DEBT_RULES.
export const MANUAL_DEBT_RULES_NO_ACCOUNT_NUMBER_MIN = MANUAL_DEBT_RULES.map((rule) =>
  rule.message === ACCOUNT_NUMBER_LENGTH_MESSAGE
    ? {
      ...rule,
      message: ACCOUNT_NUMBER_MAX_LENGTH_MESSAGE,
      isMissing: (field) => !isBlank(field?.accountNumber) && isAccountNumberTooLong(field.accountNumber)
    }
    : rule
)

export const MISSING_FIELDS_MESSAGE = "Fill in the required fields"
export const NOTHING_SAVED_MESSAGE = "No debts were saved. Please check the entered data."
export const NO_DEBTS_PERSISTED_MESSAGE = "The selected debts could not be saved. Refresh the credit report and select them again."
export const NOT_IN_CURRENT_VIEW_MESSAGE = "None of the selected debts are visible in the current view. Switch to Merged View before saving."
export const HIDDEN_SELECTION_MESSAGE = "Some selected debts are not visible in the current view and would be dropped. Switch to Merged View before saving, or unselect them."
export const SAVE_FAILED_MESSAGE = "Failed to save the selected debts. Nothing was recorded — please try again."

export const buildSelectedProfileIds = ({ selectedCreditLines, debts }) => {
  if (!Array.isArray(selectedCreditLines)) return []
  const visibleIds = new Set((debts || []).map((debt) => debt?._id))
  return selectedCreditLines
    .filter((line) => visibleIds.has(line?._id))
    .map((line) => line?._id)
    .filter(Boolean)
}

// the PUT replaces the stored array wholesale, so anything ticked but hidden by the current
// view would be dropped under a success toast. Refuse rather than narrow.
export const planCreditSelectSave = ({ selectedCreditLines, debts }) => {
  const ids = buildSelectedProfileIds({ selectedCreditLines, debts })

  if (ids.length === 0) {
    return { ids: [], error: debts?.length > 0 ? NOT_IN_CURRENT_VIEW_MESSAGE : NO_DEBTS_PERSISTED_MESSAGE }
  }

  const tickedIds = toSavedCreditProfileIds(selectedCreditLines)
  if (tickedIds.some((id) => !ids.includes(id))) {
    return { ids: [], error: HIDDEN_SELECTION_MESSAGE }
  }

  return { ids, error: null }
}

// null means the stored mode is still unknown: omitting the key lets the server's creditSelect
// merge keep it, instead of overwriting a soft-check contact with creditReportManually (WS 423808).
export const buildCreditSelectPayload = ({ isCreditCheck, reportId, selectedProfileIds, popupOnRequest }) => {
  const creditSelect = { creditReport: reportId, selectedCreditProfiles: selectedProfileIds }

  if (!popupOnRequest) {
    creditSelect.creditType = isCreditCheck ? 'softCreditCheck' : 'creditReportManually'
  } else if (isCreditCheck === true) {
    creditSelect.creditType = 'softCreditCheck'
  } else if (isCreditCheck === false) {
    creditSelect.creditType = 'creditReportManually'
  }

  return creditSelect
}

// the per-user endpoint is primary-scoped, so an enrolled co-applicant debt is legitimately
// absent from it — pruning against it would untick the debt and let the next save overwrite
// the stored pair with the primary half. Only the merged rows see the whole household.
export const reconcileSelectionToProfiles = ({ selectedCreditLines, creditProfiles, hasCoApplicant }) => {
  if (!selectedCreditLines?.length) return selectedCreditLines
  const profiles = creditProfiles || []
  return selectedCreditLines
    .filter((selected) => hasCoApplicant || profiles.some((p) => p?._id === selected?._id))
    .map((selected) => {
      const updated = profiles.find((p) => p?._id === selected?._id)
      return updated ? { ...selected, balanceAmount: updated.balanceAmount } : selected
    })
}

export const pruneSelectionToHousehold = ({ selectedCreditLines, mergedDebts }) => {
  if (!selectedCreditLines?.length) return selectedCreditLines
  // a joint duplicate collapses into one row keyed by the primary, hence coApplicantProfileId
  const householdIds = new Set(
    (mergedDebts || []).flatMap((p) => [p?._id, p?.coApplicantProfileId]).filter(Boolean)
  )
  return selectedCreditLines.filter((selected) => householdIds.has(selected?._id))
}

export const isSelectionEchoComplete = ({ sentIds, persistedIds }) => {
  const persisted = toSavedCreditProfileIds(persistedIds)
  if (persisted.length === 0) return false
  return (sentIds || []).every((id) => persisted.includes(id))
}

export const isRequiredField = (rules, key) => Boolean(rules?.find((rule) => rule.key === key)?.required)

export const hasBlockingErrors = (errors) =>
  Boolean(errors?.some((fieldErrors) => Object.values(fieldErrors || {}).some(Boolean)))

export const withAmountDefaults = (field) => ({
  ...field,
  pastDueAmount: isBlank(field?.pastDueAmount) ? 0 : field.pastDueAmount,
  creditLimit: isBlank(field?.creditLimit) ? 0 : field.creditLimit,
})

export const validateForm = ({ fields, setErrors, rules = STRICT_DEBT_RULES }) => {
  let isValid = true
  const newErrors = fields?.map(field => {
    const fieldErrors = {}

    rules.forEach((rule) => {
      if (rule.isMissing(field)) {
        fieldErrors[rule.key] = rule.message
        isValid = false
      }
    })

    return fieldErrors
  })

  setErrors(newErrors)
  return isValid
}

export const validateFormBankAccount = ({ fields, setErrors }) => {
  let isValid = true
  const fieldErrors = {}

  if (!fields.bankName) {
    fieldErrors.bankName = "Bank Name required"
    isValid = false
  }
  if (!fields.nameOnAccount) {
    fieldErrors.nameOnAccount = "Name of Bank Account required"
    isValid = false
  }
  if (fields?.routingNumber?.length !== 9) {
    fieldErrors.routingNumber = "Routing Number required"
    isValid = false
  }
  if (fields.accountNumber?.length < 3) {
    fieldErrors.accountNumber = "Account Number required"
    isValid = false
  }
  if (!fields.address) {
    fieldErrors.address = "Billing Address required"
    isValid = false
  }
  if (!fields.city) {
    fieldErrors.city = "Account Status required"
    isValid = false
  }
  if (!fields.state) {
    fieldErrors.state = "State required"
    isValid = false
  }
  if (!fields.zipcode) {
    fieldErrors.zipcode = "Zipcode required"
    isValid = false
  }

  setErrors(fieldErrors)
  return isValid
}


export const validateFormCreditCard = ({ fields, setErrors }) => {
  let isValid = true
  const fieldErrors = {}

  const cardValidation = validateDebitCardNumber(fields?.cardNumber)
  if (!cardValidation.isValid) {
    fieldErrors.cardNumber = cardValidation.error
    fieldErrors.cardNumberType = cardValidation.errorType
    isValid = false
  }
  if (!fields.nameOnCard) {
    fieldErrors.nameOnCard = "Name of Card required"
    isValid = false
  }
  if (fields?.cvv?.length < 3 || fields?.cvv?.length > 4) {
    fieldErrors.cvv = "CVV required"
    isValid = false
  } else if (fields?.provider === "American Express" && fields?.cvv?.length !== 4) {
    fieldErrors.cvv = "AmEx CVV must be 4 digits"
    isValid = false
  }
  if (!fields.expiry) {
    fieldErrors.expiry = "Expire Date required"
    isValid = false
  }
  if (!fields.provider) {
    fieldErrors.provider = "Provider required"
    isValid = false
  }
  if (!fields.address) {
    fieldErrors.address = "Billing Address required"
    isValid = false
  }
  if (!fields.city) {
    fieldErrors.city = "Account Status required"
    isValid = false
  }
  if (!fields.state) {
    fieldErrors.state = "State required"
    isValid = false
  }
  if (!fields.zipcode) {
    fieldErrors.zipcode = "Zipcode required"
    isValid = false
  }

  setErrors(fieldErrors)
  return isValid
}
